import { WebcamRecorder } from './core/MediaRecorder';
import { UploadManager } from './core/UploadManager';
import { StatusTracker } from './core/StatusTracker';
import {
  RecorderConfig,
  RecordingResult,
  UploadResult,
  ProgressEvent,
  ProcessingResult,
  ErrorEvent,
  StatusResult,
  RecordingList
} from './types';

export class VideoRecorder {
  private recorder: WebcamRecorder;
  private uploader: UploadManager;
  private tracker: StatusTracker;
  private config: RecorderConfig;

  constructor(config: RecorderConfig) {
    this.config = {
      maxDuration: 300, // 5 minutes default
      videoQuality: 'medium',
      audioEnabled: true,
      chunkSize: 5 * 1024 * 1024, // 5MB
      maxRetries: 3,
      parallelUploads: 3,
      pollingIntervalMs: 2000, // 2 seconds default
      outputFormats: ['hls', 'mp4'],
      thumbnailCount: 3,
      showPreview: true,
      ...config
    };

    this.recorder = new WebcamRecorder(this.config);
    this.uploader = new UploadManager(this.config);
    this.tracker = new StatusTracker(this.config);
  }

  // Configuration
  configure(options: Partial<RecorderConfig>): void {
    // Update config
    this.config = { ...this.config, ...options };
    
    // Update recorder with new config (preserves stream and preview)
    this.recorder.updateConfig(this.config);
    
    // Recreate uploader and tracker with new config
    this.uploader = new UploadManager(this.config);
    this.tracker = new StatusTracker(this.config);
  }

  // Recording lifecycle
  async initialize(): Promise<void> {
    await this.recorder.initialize();
  }

  // Preview management
  setPreviewElement(element: HTMLVideoElement): void {
    this.recorder.setPreviewElement(element);
  }
  async startRecording(): Promise<void> {
    await this.recorder.startRecording();
  }

  async stopRecording(): Promise<RecordingResult> {
    return this.recorder.stopRecording();
  }

  pauseRecording(): void {
    this.recorder.pauseRecording();
  }

  resumeRecording(): void {
    this.recorder.resumeRecording();
  }

  // Upload management
  async uploadRecording(file: Blob, metadata?: Record<string, unknown>): Promise<UploadResult> {
    const result = await this.uploader.uploadRecording(file, metadata);
    // Start polling for processing status
    this.tracker.startPolling(result.trackingId);
    return result;
  }

  async retryUpload(trackingId: string): Promise<UploadResult> {
    return this.uploader.retryUpload(trackingId);
  }

  async cancelUpload(trackingId: string): Promise<void> {
    await this.uploader.cancelUpload(trackingId);
    this.tracker.stopPolling(trackingId);
  }

  // Progress tracking
  onProgress(callback: (progress: ProgressEvent) => void): void {
    this.uploader.onProgress(callback);
  }

  onComplete(callback: (result: ProcessingResult) => void): void {
    this.tracker.onComplete(callback);
  }

  onError(callback: (error: ErrorEvent) => void): void {
    this.tracker.onError(callback);
  }

  // Status queries
  async getUploadStatus(trackingId: string): Promise<StatusResult> {
    return this.tracker.getUploadStatus(trackingId);
  }

  async listRecordings(userId?: string): Promise<RecordingList> {
    return this.tracker.listRecordings(userId);
  }

  // Utility methods
  getRecordingState(): ReturnType<WebcamRecorder['getRecordingState']> {
    return this.recorder.getRecordingState();
  }

  /**
   * Records video and automatically uploads when complete.
   * Uses the existing event system for progress tracking and completion.
   */
  async recordAndUpload(
      metadata?: Record<string, unknown>
  ): Promise<{ recording: RecordingResult; upload: UploadResult }> {
    // Start recording
    await this.startRecording();
    
    // Wait for recording to complete (either manually stopped or max duration reached)
    const recording = await this.waitForRecordingComplete();
    
    // Upload the recording
    const upload = await this.uploadRecording(recording.blob, metadata);
    
    return { recording, upload };
  }

  /**
   * Waits for recording to complete either by manual stop or max duration
   */
  private async waitForRecordingComplete(): Promise<RecordingResult> {
    return new Promise((resolve, reject) => {
      const checkRecordingState = () => {
        const state = this.getRecordingState();
        
        // Check if recording stopped manually
        if (!state.isRecording) {
          // Recording was stopped externally, resolve immediately
          this.stopRecording().then(resolve).catch(reject);
          return;
        }
        
        // Check if max duration reached
        if (this.config.maxDuration && state.duration >= this.config.maxDuration) {
          this.stopRecording().then(resolve).catch(reject);
          return;
        }
        
        // Continue checking
        setTimeout(checkRecordingState, 1000);
      };
      
      // Start checking after a short delay
      setTimeout(checkRecordingState, 1000);
    });
  }

  // Cleanup
  cleanup(): void {
    this.recorder.cleanup();
    this.uploader.cleanup();
    this.tracker.cleanup();
  }

  // Static utility methods
  static async checkBrowserSupport(): Promise<{
    supported: boolean;
    features: {
      mediaRecorder: boolean;
      getUserMedia: boolean;
      webrtc: boolean;
    };
    recommendations?: string[];
  }> {
    // Guard for non-browser environments
    const hasWindow = typeof window !== 'undefined';
    const hasNavigator = typeof navigator !== 'undefined';

    const features = {
      mediaRecorder: hasWindow && 'MediaRecorder' in window,
      getUserMedia:
          hasNavigator &&
          !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia),
      // Vendor-prefixed peer connection detection with safe casting
      webrtc:
          hasWindow &&
          (!!(window as unknown as { RTCPeerConnection?: unknown }).RTCPeerConnection ||
              !!(window as unknown as { webkitRTCPeerConnection?: unknown })
                  .webkitRTCPeerConnection)
    };

    const supported = features.mediaRecorder && features.getUserMedia;
    const recommendations: string[] = [];

    if (!features.mediaRecorder) {
      recommendations.push(
          'MediaRecorder API not supported. Consider using a WebRTC-based fallback.'
      );
    }

    if (!features.getUserMedia) {
      recommendations.push('getUserMedia not supported. Camera access unavailable.');
    }

    return recommendations.length
        ? { supported, features, recommendations }
        : { supported, features };
  }

  static getRecommendedSettings(
      deviceType: 'mobile' | 'desktop' = 'desktop'
  ): Partial<RecorderConfig> {
    if (deviceType === 'mobile') {
      return {
        videoQuality: 'medium',
        maxDuration: 180, // 3 minutes
        chunkSize: 2 * 1024 * 1024, // 2MB
        parallelUploads: 2
      };
    }

    return {
      videoQuality: 'high',
      maxDuration: 600, // 10 minutes
      chunkSize: 10 * 1024 * 1024, // 10MB
      parallelUploads: 4
    };
  }
}
